import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { QuoteToolLiteStore } from '../store';
import { CoursePayload } from '../types';
import { QuoteInputMapper } from './quote-input.mapper';
import { ResidenceDetails } from '../types/residence-details.type';
import { Flows, Flow, DeeplinkRequest, PreselectedMode, PreselectedModeEnum, DeeplinkTypeEnum } from '@lx/quote-tool/lite/shared/types';
import { DeeplinkGeneratorService as SharedDeeplinkService } from '@lx/quote-tool/lite/shared/services';
import { CountryDetectorService } from '@lx/quote-tool/shared/services';

@Injectable({
  providedIn: 'root',
})
export class DeeplinkGeneratorService {
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  readonly isValid$ = this.quoteToolLiteStore.validationData$.pipe(
    switchMap((validationData) => this.isFlowDataValid(validationData.activeFlow, validationData)),
  );
  private readonly sharedDeeplinkService = inject(SharedDeeplinkService);
  private readonly countryDetector = inject(CountryDetectorService);
  private readonly quoteInputMapper = inject(QuoteInputMapper);

  /**
   * Generates a deeplink with the current course details
   * Uses shared service to call the backend API
   * @returns Observable that emits the generated deeplink URL or null if generation fails
   */
  generateDeeplink(): Observable<string | null> {
    return this.quoteToolLiteStore.courseDetails$.pipe(
      take(1),
      map((courseDetails) => ({
        activeFlow: courseDetails.activeFlow,
        courseDetails,
      })),
      switchMap(({ activeFlow, courseDetails }) => {
        const request = this.buildDeeplinkRequest({ activeFlow, courseDetails });
        return this.sharedDeeplinkService.generateDeeplink(request);
      }),
    );
  }

  /**
   * Builds a deeplink request from course details and flow information
   * @param data Course details and flow information
   * @returns DeeplinkRequest ready for API submission
   */
  private buildDeeplinkRequest({ activeFlow, courseDetails }: { activeFlow: Flow; courseDetails: CoursePayload }): DeeplinkRequest {
    const selectedDuration = activeFlow === Flows.Aya ? courseDetails.selectedAyaDuration?.weeks : courseDetails.selectedDuration;
    const quoteInput = this.quoteInputMapper.mapQuoteInput({ ...courseDetails, selectedDuration, selectedResidence: this.getResidenceData() });

    const request: DeeplinkRequest = {
      quoteInput,
      preselectedMode: this.mapFlowToMode(activeFlow),
      deeplinkType: DeeplinkTypeEnum.COPY_SELECTION,
    };

    const { selectedLanguage } = courseDetails;
    if (selectedLanguage) {
      request.studyLanguage = selectedLanguage;
    }

    const onlineFlow = activeFlow === Flows.Online;
    if (onlineFlow) {
      this.addClassTimeToRequest(request, courseDetails);
    }

    return request;
  }

  /**
   * Adds class time information to the deeplink request for online flows
   * @param request The deeplink request object to update
   * @param courseDetails Course details containing class time information
   */
  private addClassTimeToRequest(request: DeeplinkRequest, courseDetails: CoursePayload): void {
    const { selectedClassTime } = courseDetails;

    if (!selectedClassTime) {
      return;
    }

    const { startTime, endTime } = selectedClassTime;

    if (startTime) {
      request.classStartTime = this.formatTimeWithoutColons(startTime);
    }

    if (endTime) {
      request.classEndTime = this.formatTimeWithoutColons(endTime);
    }
  }

  private getResidenceData(): ResidenceDetails {
    return { countryCode: this.countryDetector.detectCountryCode() || undefined, phoneCode: '', currency: '' };
  }

  /**
   * Validates flow-specific data requirements
   * @param activeFlow Current flow type
   * @param courseDetails Course details to validate
   * @returns Observable emitting validation state
   */
  private isFlowDataValid(activeFlow: Flow | undefined, courseDetails: CoursePayload): Observable<boolean> {
    const duration = courseDetails.selectedDuration ?? courseDetails.selectedAyaDuration?.weeks;

    const { selectedCourse, selectedSchoolLocation, selectedLanguage, selectedAyaDuration } = courseDetails;

    // Basic required fields for all flows
    if (!selectedCourse?.code || !selectedSchoolLocation?.schoolCode) {
      return of(false);
    }

    // Flow-specific validations
    switch (activeFlow) {
      case Flows.Junior:
        return of(!!selectedLanguage);

      case Flows.Aya:
        return of(!!selectedLanguage && !!selectedAyaDuration?.weeks);

      case Flows.Online:
        return of(!!duration);

      case Flows.Adult:
      default:
        return of(!!selectedLanguage && !!duration);
    }
  }

  /**
   * Formats time by removing colons (e.g., "09:00" -> "0900")
   * @param time Time string with colons
   * @returns Formatted time without colons
   */
  private formatTimeWithoutColons(time: string): string {
    return time.replace(/:/g, '');
  }

  /**
   * Maps flow type to the corresponding API mode enum
   * @param flow Current flow type
   * @returns Corresponding PreselectedMode
   */
  private mapFlowToMode(flow: Flow | undefined): PreselectedMode | undefined {
    switch (flow) {
      case Flows.Junior:
        return PreselectedModeEnum.JUNIOR_CAMP;
      case Flows.Online:
        return PreselectedModeEnum.ONLINE_COURSE;
      case Flows.Aya:
        return PreselectedModeEnum.ACADEMIC_YEAR_ABROAD;
      default:
        return PreselectedModeEnum.STUDY_AND_STAY;
    }
  }
}
